import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { HINT_CONFIG, LEVEL_ORDER, LEVEL_HINT_VISIBILITY, RHIE_GUIDE_MESSAGES } from '../config/zihinKilavuzuConfig';
import { zihinEventBus, ZIHIN_EVENTS, rhieEventBus, RHIE_EVENTS } from '../services/zihinKilavuzuEventBus';

const ZihinKilavuzuContext = createContext(null);

// LocalStorage keys
const STORAGE_KEYS = {
  SEEN_HINTS: 'zihinKilavuzu_seenHints',
  DAILY_COUNT: 'zihinKilavuzu_dailyCount',
  DAILY_DATE: 'zihinKilavuzu_dailyDate',
  DISABLED: 'zihinKilavuzu_disabled'
};

// Daily hint limit
const DAILY_HINT_LIMIT = 10;

export function ZihinKilavuzuProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  
  // State
  const [activeHint, setActiveHint] = useState(null);
  const [hintQueue, setHintQueue] = useState([]);
  const [seenHints, setSeenHints] = useState([]);
  const [dailyCount, setDailyCount] = useState(0);
  const [hintsDisabled, setHintsDisabled] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  
  // Refs
  const autoDismissTimer = useRef(null);
  const observerRef = useRef(null);
  
  // Get user level
  const userLevel = user?.rank || 'Farkındalık';
  const levelConfig = LEVEL_HINT_VISIBILITY[userLevel] || LEVEL_HINT_VISIBILITY['Farkındalık'];
  
  // Initialize from localStorage
  useEffect(() => {
    try {
      const storedSeenHints = localStorage.getItem(STORAGE_KEYS.SEEN_HINTS);
      if (storedSeenHints) {
        setSeenHints(JSON.parse(storedSeenHints));
      }
      
      const storedDisabled = localStorage.getItem(STORAGE_KEYS.DISABLED);
      if (storedDisabled === 'true') {
        setHintsDisabled(true);
      }
      
      // Check daily count
      const storedDate = localStorage.getItem(STORAGE_KEYS.DAILY_DATE);
      const today = new Date().toDateString();
      
      if (storedDate === today) {
        const storedCount = localStorage.getItem(STORAGE_KEYS.DAILY_COUNT);
        setDailyCount(parseInt(storedCount) || 0);
      } else {
        // New day, reset count
        localStorage.setItem(STORAGE_KEYS.DAILY_DATE, today);
        localStorage.setItem(STORAGE_KEYS.DAILY_COUNT, '0');
        setDailyCount(0);
      }
    } catch (err) {
      console.error('Error loading ZihinKilavuzu state:', err);
    }
  }, []);
  
  // Save seenHints to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SEEN_HINTS, JSON.stringify(seenHints));
    } catch (err) {
      console.error('Error saving seenHints:', err);
    }
  }, [seenHints]);
  
  // Check if hint should be shown based on level
  const shouldShowForLevel = useCallback((hint) => {
    const minIndex = LEVEL_ORDER.indexOf(hint.level?.min || 'Farkındalık');
    const maxIndex = LEVEL_ORDER.indexOf(hint.level?.max || 'Mentor');
    const userIndex = LEVEL_ORDER.indexOf(userLevel);
    
    return userIndex >= minIndex && userIndex <= maxIndex;
  }, [userLevel]);
  
  // Check if hint can be shown
  const canShowHint = useCallback((hint) => {
    if (hintsDisabled) return false;
    if (dailyCount >= DAILY_HINT_LIMIT) return false;
    if (hint.behavior?.showOnce && seenHints.includes(hint.id)) return false;
    if (!shouldShowForLevel(hint)) return false;
    
    return true;
  }, [hintsDisabled, dailyCount, seenHints, shouldShowForLevel]);
  
  // Dismiss current hint - defined first to avoid dependency issues
  const dismissHintInternal = useCallback((hintId, currentActiveHint, currentSeenHints, currentHintQueue, showHintFn) => {
    if (autoDismissTimer.current) {
      clearTimeout(autoDismissTimer.current);
    }
    
    setActiveHint(null);
    
    if (currentActiveHint?.behavior?.showOnce && !currentSeenHints.includes(hintId)) {
      setSeenHints(prev => [...prev, hintId]);
    }
    
    zihinEventBus.emit(ZIHIN_EVENTS.HINT_DISMISSED, { hintId });
    
    // Process next in queue
    if (currentHintQueue.length > 0) {
      const [nextHint, ...rest] = currentHintQueue;
      setHintQueue(rest);
      if (showHintFn) {
        setTimeout(() => showHintFn(nextHint), 500);
      }
    }
  }, []);
  
  // Show a hint
  const showHint = useCallback((hint) => {
    if (!canShowHint(hint)) return false;
    
    // Check if target element exists
    const targetEl = document.querySelector(hint.targetSelector);
    if (!targetEl && hint.targetSelector !== '*') {
      console.log(`ZihinKilavuzu: Target not found for hint ${hint.id}`);
      return false;
    }
    
    setActiveHint({ ...hint, targetElement: targetEl });
    setDailyCount(prev => {
      const newCount = prev + 1;
      localStorage.setItem(STORAGE_KEYS.DAILY_COUNT, newCount.toString());
      return newCount;
    });
    
    // Auto-dismiss if configured
    if (hint.behavior?.autoDismissMs > 0) {
      autoDismissTimer.current = setTimeout(() => {
        setActiveHint(null);
        setSeenHints(prev => hint.behavior?.showOnce ? [...prev, hint.id] : prev);
        zihinEventBus.emit(ZIHIN_EVENTS.HINT_DISMISSED, { hintId: hint.id });
      }, hint.behavior.autoDismissMs);
    }
    
    // Emit event
    zihinEventBus.emit(ZIHIN_EVENTS.HINT_SHOWN, { hintId: hint.id });
    
    return true;
  }, [canShowHint]);
  
  // Dismiss wrapper function
  const dismissHint = useCallback((hintId) => {
    dismissHintInternal(hintId, activeHint, seenHints, hintQueue, showHint);
  }, [activeHint, seenHints, hintQueue, showHint, dismissHintInternal]);
  
  // Queue a hint
  const queueHint = useCallback((hint) => {
    if (!canShowHint(hint)) return;
    
    if (activeHint) {
      setHintQueue(prev => [...prev, hint]);
    } else {
      showHint(hint);
    }
  }, [activeHint, canShowHint, showHint]);
  
  // Start onboarding
  const startOnboarding = useCallback(() => {
    const onboardingHints = HINT_CONFIG.onboarding
      .filter(h => canShowHint(h))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    
    if (onboardingHints.length > 0) {
      setIsOnboarding(true);
      setOnboardingStep(0);
      showHint(onboardingHints[0]);
    }
  }, [canShowHint, showHint]);
  
  // Skip onboarding
  const skipOnboarding = useCallback(() => {
    setIsOnboarding(false);
    setActiveHint(null);
    setHintQueue([]);
    
    // Mark all onboarding hints as seen
    const onboardingIds = HINT_CONFIG.onboarding.map(h => h.id);
    setSeenHints(prev => [...new Set([...prev, ...onboardingIds])]);
  }, []);
  
  // Toggle hints
  const toggleHints = useCallback((enabled) => {
    setHintsDisabled(!enabled);
    localStorage.setItem(STORAGE_KEYS.DISABLED, (!enabled).toString());
  }, []);
  
  // Reset hints
  const resetHints = useCallback(() => {
    setSeenHints([]);
    setDailyCount(0);
    localStorage.removeItem(STORAGE_KEYS.SEEN_HINTS);
    localStorage.setItem(STORAGE_KEYS.DAILY_COUNT, '0');
  }, []);
  
  // Check page hints on location change
  useEffect(() => {
    if (hintsDisabled || !user) return;
    
    const currentPath = location.pathname;
    
    // Find hints for current page
    const pageHints = [
      ...HINT_CONFIG.onboarding,
      ...HINT_CONFIG.contextual
    ].filter(hint => {
      if (hint.page === '*') return true;
      if (hint.page === currentPath) return true;
      if (currentPath.startsWith(hint.page)) return true;
      return false;
    });
    
    // Process onFirstVisit hints
    pageHints
      .filter(h => h.trigger?.type === 'onFirstVisit' && canShowHint(h))
      .forEach(h => {
        setTimeout(() => queueHint(h), 1000);
      });
    
  }, [location.pathname, user, hintsDisabled, canShowHint, queueHint]);
  
  // Listen for event-triggered hints
  useEffect(() => {
    const eventHints = HINT_CONFIG.contextual.filter(
      h => h.trigger?.type === 'onEvent'
    );
    
    const unsubscribes = eventHints.map(hint => {
      return zihinEventBus.on(hint.trigger.event, () => {
        if (canShowHint(hint)) {
          queueHint(hint);
        }
      });
    });
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [canShowHint, queueHint]);
  
  // Listen for R.H.I.E. events and show contextual hints
  useEffect(() => {
    const rhieUnsubscribes = [];
    
    // Helper to show guide message based on level
    const showRhieGuide = (event, data = {}) => {
      const messages = RHIE_GUIDE_MESSAGES[event];
      if (!messages) return;
      
      const message = messages[userLevel];
      if (!message) return; // No message for this level (e.g., Mentor)
      
      let body = message.body;
      // Handle dynamic body content
      if (typeof body === 'function') {
        body = body(data.value || data.xp || data.days || data.level || data.badge || data.currentStreak);
      }
      
      // Create and show hint
      const hint = {
        id: `rhie_${event}_${Date.now()}`,
        targetSelector: '*',
        copy: {
          title: message.title,
          body: body
        },
        behavior: { showOnce: false, autoDismissMs: message.duration || 4000, blockInteraction: false },
        position: 'top-center'
      };
      
      queueHint(hint);
    };
    
    // Subscribe to R.H.I.E. ritual events
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.RITUAL_COMPLETED, (data) => {
        showRhieGuide(RHIE_EVENTS.RITUAL_COMPLETED, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_EXTENDED, (data) => {
        showRhieGuide(RHIE_EVENTS.STREAK_EXTENDED, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_BROKEN, (data) => {
        showRhieGuide(RHIE_EVENTS.STREAK_BROKEN, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_MILESTONE, (data) => {
        showRhieGuide(RHIE_EVENTS.STREAK_MILESTONE, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.XP_EARNED, (data) => {
        // Only show for larger XP gains
        if (data.xp >= 10) {
          showRhieGuide(RHIE_EVENTS.XP_EARNED, data);
        }
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.LEVEL_UP, (data) => {
        showRhieGuide(RHIE_EVENTS.LEVEL_UP, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.BADGE_EARNED, (data) => {
        showRhieGuide(RHIE_EVENTS.BADGE_EARNED, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_AT_RISK, (data) => {
        showRhieGuide(RHIE_EVENTS.STREAK_AT_RISK, data);
      })
    );
    
    rhieUnsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.AI_DROP_RISK_DETECTED, (data) => {
        showRhieGuide(RHIE_EVENTS.AI_DROP_RISK_DETECTED, data);
      })
    );
    
    return () => {
      rhieUnsubscribes.forEach(unsub => unsub());
    };
  }, [userLevel, queueHint]);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (autoDismissTimer.current) {
        clearTimeout(autoDismissTimer.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  
  const value = {
    activeHint,
    showHint,
    dismissHint,
    queueHint,
    startOnboarding,
    skipOnboarding,
    isOnboarding,
    onboardingStep,
    hintsDisabled,
    toggleHints,
    resetHints,
    seenHints,
    dailyCount,
    userLevel,
    levelConfig
  };
  
  return (
    <ZihinKilavuzuContext.Provider value={value}>
      {children}
    </ZihinKilavuzuContext.Provider>
  );
}

export function useZihinKilavuzu() {
  const context = useContext(ZihinKilavuzuContext);
  if (!context) {
    throw new Error('useZihinKilavuzu must be used within ZihinKilavuzuProvider');
  }
  return context;
}

export default ZihinKilavuzuContext;
